import { Controller } from "@hotwired/stimulus"
import { Sortable } from "@shopify/draggable"

export default class extends Controller {
  static targets = ["status"]
  static values = { url: String }

  connect() {
    this.startSortable()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  startSortable() {
    this.sortable = new Sortable(this.element, {
      draggable: "tr[data-event-id]",
      handle: "[data-event-order-handle]",
      mirror: { constrainDimensions: true }
    })

    this.sortable.on("drag:start", this.captureOrder)
    this.sortable.on("sortable:sort", this.showDropTarget)
    this.sortable.on("drag:stop", this.clearDropTarget)
    this.sortable.on("sortable:stop", this.saveOrder)
  }

  captureOrder = () => {
    this.orderBeforeDrag = this.eventIds()
  }

  showDropTarget = (event) => {
    this.clearDropTarget()
    event.over?.classList.add("event-order-drop-target")
  }

  clearDropTarget = () => {
    this.element.querySelectorAll(".event-order-drop-target").forEach((row) => {
      row.classList.remove("event-order-drop-target")
    })
  }

  saveOrder = async () => {
    await new Promise(requestAnimationFrame)

    if (this.saving || this.ordersMatch(this.orderBeforeDrag, this.eventIds())) return

    this.saving = true
    this.sortable.destroy()
    this.setStatus("Saving website order…")

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({ event_ids: this.eventIds() })
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) throw new Error(payload.error || "The event order could not be saved.")

      this.setStatus("Website order saved.")
    } catch (error) {
      this.restoreOrder()
      this.setStatus(error.message, true)
    } finally {
      this.saving = false
      this.startSortable()
    }
  }

  eventIds() {
    const ids = Array.from(this.element.children)
      .filter((row) => row.matches("tr[data-event-id]"))
      .map((row) => row.dataset.eventId)

    return ids.filter((eventId, index) => ids.indexOf(eventId) === index)
  }

  restoreOrder() {
    if (!this.orderBeforeDrag) return

    const rowsById = new Map(
      Array.from(this.element.children)
        .filter((row) => row.matches("tr[data-event-id]"))
        .map((row) => [row.dataset.eventId, row])
    )

    this.orderBeforeDrag.forEach((eventId) => this.element.append(rowsById.get(eventId)))
  }

  ordersMatch(firstOrder, secondOrder) {
    return firstOrder?.length === secondOrder.length && firstOrder.every((eventId, index) => eventId === secondOrder[index])
  }

  setStatus(message, error = false) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-red-700", error)
    this.statusTarget.classList.toggle("text-emerald-700", !error)
  }
}
